
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, LoadingState, EmptyState } from "@/components/features/states";
import { toast } from "sonner";
import {
  CheckCircle2,
  XCircle,
  Clock,
  Store,
  Bike,
  FileText,
} from "lucide-react";
import type { RoleApplication } from "@/lib/types";

export function AdminRoleApplicationsView() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<"pending" | "approved" | "rejected" | "all">("pending");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-role-applications", statusFilter],
    queryFn: () =>
      api.admin.roleApplications(statusFilter === "all" ? undefined : { status: statusFilter }),
  });
  const applications = data?.data?.applications ?? [];

  const reviewMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { action: "approve" | "reject"; reason?: string } }) =>
      api.admin.reviewRoleApplication(id, body),
    onSuccess: () => {
      toast.success(t("adminRoleApplications.reviewed"));
      qc.invalidateQueries({ queryKey: ["admin-role-applications"] });
    },
    onError: (e: any) => toast.error(e?.message || t("common.error")),
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title={t("adminRoleApplications.title")}
        subtitle={t("adminRoleApplications.subtitle")}
      />

      {/* Status filter */}
      <div className="flex gap-1.5 overflow-x-auto scroll-area pb-1">
        {(["pending", "approved", "rejected", "all"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
              statusFilter === s
                ? "bg-primary text-primary-foreground shadow"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            {t(`adminRoleApplications.filter_${s}`)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <LoadingState label={t("common.loading")} />
      ) : applications.length === 0 ? (
        <EmptyState title={t("adminRoleApplications.empty")} />
      ) : (
        <div className="space-y-3">
          {applications.map((app) => (
            <ApplicationReviewCard
              key={app.id}
              app={app}
              onReview={(body) => reviewMutation.mutate({ id: app.id, body })}
              loading={reviewMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ApplicationReviewCard({
  app,
  onReview,
  loading,
}: {
  app: RoleApplication;
  onReview: (body: { action: "approve" | "reject"; reason?: string }) => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");

  const isProvider = app.role === "provider";
  const Icon = isProvider ? Store : Bike;
  const statusConfig = {
    pending: { icon: Clock, color: "text-amber-600", bg: "bg-amber-500/10", label: t("adminRoleApplications.statusPending") },
    approved: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10", label: t("adminRoleApplications.statusApproved") },
    rejected: { icon: XCircle, color: "text-red-600", bg: "bg-red-500/10", label: t("adminRoleApplications.statusRejected") },
  };
  const cfg = statusConfig[app.status as keyof typeof statusConfig] ?? statusConfig.pending;
  const StatusIcon = cfg.icon;

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-bold">
                {isProvider ? t("roleApplication.provider") : t("roleApplication.courier")}
              </p>
              <p className="text-xs text-muted-foreground">
                {app.user?.name} · {app.user?.email}
              </p>
              {app.user?.phone && (
                <p className="text-xs text-muted-foreground">{app.user.phone}</p>
              )}
            </div>
          </div>
          <Badge className={`${cfg.bg} ${cfg.color} hover:${cfg.bg}`}>
            <StatusIcon className="mr-0.5 h-3 w-3" /> {cfg.label}
          </Badge>
        </div>

        {/* Documents */}
        <div className="rounded-lg bg-muted/40 p-3">
          <p className="mb-1.5 flex items-center gap-1 text-xs font-semibold text-muted-foreground">
            <FileText className="h-3.5 w-3.5" /> {t("adminRoleApplications.documents")}
          </p>
          <div className="space-y-1 text-xs">
            {isProvider ? (
              <>
                <DocRow label={t("roleApplication.businessName")} value={app.businessName} />
                <DocRow label={t("roleApplication.taxId")} value={app.taxId} />
                <DocRow label={t("roleApplication.storePhotoUrl")} value={app.storePhotoUrl} link />
                {app.bankingDetails && (
                  <DocRow label={t("roleApplication.bankingDetails")} value={app.bankingDetails} />
                )}
              </>
            ) : (
              <>
                <DocRow label={t("roleApplication.licenseNumber")} value={app.licenseNumber} />
                <DocRow label={t("roleApplication.vehicleType")} value={app.vehicleType} />
                <DocRow label={t("roleApplication.licensePhotoUrl")} value={app.licensePhotoUrl} link />
                <DocRow label={t("roleApplication.vehicleInsuranceUrl")} value={app.vehicleInsuranceUrl} link />
                <DocRow label={t("roleApplication.vehiclePhotoUrl")} value={app.vehiclePhotoUrl} link />
              </>
            )}
            <p className="pt-1 text-muted-foreground">
              {t("adminRoleApplications.submitted")}: {new Date(app.submittedAt).toLocaleString()}
            </p>
          </div>
        </div>

        {app.rejectionReason && (
          <p className="rounded-md bg-red-50 p-2 text-xs text-red-700 dark:bg-red-950/30 dark:text-red-300">
            {app.rejectionReason}
          </p>
        )}

        {/* Actions */}
        {app.status === "pending" && (
          <div className="space-y-2">
            {!rejectOpen ? (
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  disabled={loading}
                  onClick={() => onReview({ action: "approve" })}
                >
                  <CheckCircle2 className="mr-1.5 h-4 w-4" /> {t("adminRoleApplications.approve")}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={loading}
                  onClick={() => setRejectOpen(true)}
                >
                  <XCircle className="mr-1.5 h-4 w-4" /> {t("adminRoleApplications.reject")}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>{t("adminRoleApplications.rejectionReason")}</Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t("adminRoleApplications.rejectionPlaceholder")}
                  rows={2}
                />
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => { setRejectOpen(false); setReason(""); }}>
                    {t("common.cancel")}
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    disabled={!reason.trim() || loading}
                    onClick={() => onReview({ action: "reject", reason })}
                  >
                    {t("adminRoleApplications.confirmReject")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DocRow({ label, value, link }: { label: string; value: string | null; link?: boolean }) {
  const { t } = useTranslation();
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <span className="shrink-0 font-medium text-muted-foreground">{label}:</span>
      {link ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
          {t("adminRoleApplications.viewDoc")}
        </a>
      ) : (
        <span className="font-medium">{value}</span>
      )}
    </div>
  );
}
